import {
  Component,
  ElementRef,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';

import {
  EntityFieldDef,
  CellUpdateEvent,
} from '../../models/spreadsheet.model';
import { UniverSpreadsheetService } from '../../services/univer-spreadsheet.service';
import { SPREADSHEET_CONFIG } from '../../config/spreadsheet.config';

/**
 * @class SpreadsheetViewerComponent
 * @description The UI wrapper for the Univer instance. Binds Angular inputs to the Univer service.
 */
@Component({
  selector: 'rapider-spreadsheet-viewer',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './spreadsheet-viewer.component.html',
})
export class SpreadsheetViewerComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  @ViewChild('univerContainer', { static: false })
  univerContainer!: ElementRef<HTMLElement>;

  @Input({ required: true }) data: any[] = [];
  @Input({ required: true }) fields: EntityFieldDef[] = [];
  @Input({ required: true }) name: string = '';

  // Expose Dark Mode toggle to parent components
  @Input() darkMode: boolean = false;

  @Output() cellUpdated = new EventEmitter<CellUpdateEvent>();
  @Output() rowSelected = new EventEmitter<any>();
  @Output() rowsDeleted = new EventEmitter<any[]>();

  public config = SPREADSHEET_CONFIG;
  private univerService = inject(UniverSpreadsheetService);
  private updateSub!: Subscription;
  private selectionSub!: Subscription;
  private deletedRowsSub!: Subscription;

  // CRITICAL FIX: Convert to an Angular Signal to prevent NG0100 ExpressionChangedAfterItHasBeenCheckedError
  public isInitialized = signal<boolean>(false);

  ngAfterViewInit(): void {
    // Initialize the Univer engine into the container with the initial dark mode state
    this.univerService.init(this.univerContainer.nativeElement, this.darkMode);

    // CRITICAL FIX: Subscribe to valid cell updates and emit them up to the parent/backend
    this.updateSub = this.univerService.cellUpdates$.subscribe((event) => {
      this.cellUpdated.emit(event);
    });

    // Subscribe to selections to display active records
    this.selectionSub = this.univerService.selectionUpdates$.subscribe(
      (record: any) => {
        this.rowSelected.emit(record);
      },
    );

    // Subscribe to deleted rows events
    this.deletedRowsSub = this.univerService.deletedRows$.subscribe(
      (records) => {
        this.rowsDeleted.emit(records);
      },
    );

    // CRITICAL FIX: Unconditionally load the workbook. Even if data is empty,
    // we must generate the headers and create an empty grid for the user to enter new records!
    this.univerService.loadData(
      this.data || [],
      this.fields || [],
      this.name ?? 'NewSpreadsheet',
    );

    // Delay dismissing the loading screen slightly to allow the canvas to fully render and attach
    setTimeout(() => {
      this.isInitialized.set(true);
    }, 300);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reactive updates: if data or fields change after init, reload the workbook
    if (this.isInitialized() && (changes['data'] || changes['fields'])) {
      // Unconditionally load to support wiping the grid empty
      this.univerService.loadData(
        this.data || [],
        this.fields || [],
        this.name ?? 'NewSpreadsheet',
      );
    }

    // Dynamically update dark mode if it changes without reloading the canvas
    if (
      this.isInitialized() &&
      changes['darkMode'] &&
      !changes['darkMode'].firstChange
    ) {
      this.univerService.setDarkMode(changes['darkMode'].currentValue);
    }
  }

  /**
   * Proxies the auto-size command to the Univer engine to fit all columns to their data content.
   */
  public autoSizeColumns(): void {
    this.univerService.autoSizeColumns();
  }

  /**
   * Proxies a command to the Univer engine to hide or show specific column indices.
   * @param {number[]} indices - The array of column indices.
   * @param {boolean} isVisible - True to show, false to hide.
   */
  public toggleColumnsVisibility(indices: number[], isVisible: boolean): void {
    this.univerService.toggleColumnsVisibility(indices, isVisible);
  }

  /**
   * Proxies a command to the Univer engine to highlight a specific row and target cells.
   * @param {string | number} rowIndicator - The unique record ID or new row indicator.
   * @param {'changed' | 'error' | 'clear' | 'new_ready' | 'new_error'} status - The target visual state.
   * @param {string[]} changedFields - Array of keys indicating which specific cells changed.
   * @param {string[]} errorFields - Array of keys indicating cells that failed validation.
   */
  public setRowHighlight(
    rowIndicator: string | number,
    status: 'changed' | 'error' | 'clear' | 'new_ready' | 'new_error',
    changedFields: string[] = [],
    errorFields: string[] = [],
  ): void {
    this.univerService.setRowHighlight(
      rowIndicator,
      status,
      changedFields,
      errorFields,
    );
  }

  /**
   * Proxies a command to the Univer engine to revert pending changes natively.
   * @param {Map<string, any>} pendingChanges - Map of changes containing original values and target rows.
   */
  public revertChanges(pendingChanges: Map<string, any>): void {
    this.univerService.revertChanges(pendingChanges);
  }

  public setCellValues(
    matrix: any,
    range: {
      startRow: number;
      endRow: number;
      startColumn: number;
      endColumn: number;
    },
  ): void {
    this.univerService.setCellValues(matrix, range);
  }

  public restoreDeletedRows(records: any[]): void {
    this.univerService.restoreDeletedRows(records);
  }

  ngOnDestroy(): void {
    if (this.updateSub) {
      this.updateSub.unsubscribe();
    }
    if (this.selectionSub) {
      this.selectionSub.unsubscribe();
    }
    if (this.deletedRowsSub) {
      this.deletedRowsSub.unsubscribe();
    }
    // Note: UniverService has its own ngOnDestroy that cleans up the instance.
  }
}
